import React, { Fragment, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatPrice } from 'utilities/strings';

import Button from '@material-ui/core/Button';
import ChevronDownIcon from 'mdi-material-ui/ChevronDown';
import Collapse from '@material-ui/core/Collapse';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

const OrderItemWrapper = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  margin-bottom: 10px;
  padding-bottom: 10px;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const TotalInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 10px;
`;

const StyledCollapse = styled(Collapse)`
  position: relative;
  &:after {
    pointer-events: none;
    transition: all 1s linear;
    //Using the collapse 'in' prop to show the gradient when collapsed
    background: linear-gradient(
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, ${props => (props.in ? 0 : 1)})
    );
    position: absolute;
    z-index: 1;
    display: block;
    width: 100%;
    height: 80px;
    content: '';
    bottom: 0;
  }
`;

const OrderText = styled(Typography)`
  font-size: 12px;
  top: -1px;
  margin-left: 12px;
  font-weight: normal;
  white-space: nowrap;
`;

const OrderItems = ({ groups = [], subtotal, is_distributor, isLocked }) => {
  const maxHeight = 340;
  const collapseEl = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [expandable, setExpandable] = useState(false);

  useEffect(() => {
    if (
      collapseEl.current &&
      collapseEl.current.wrapperRef.clientHeight > maxHeight
    ) {
      setExpanded(false);
      setExpandable(true);
    } else {
      setExpanded(true);
      setExpandable(false);
    }
  }, [groups]);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Items in Order
      </Typography>
      {groups.map((group, idx) => (
        <Fragment key={idx}>
          <StyledCollapse
            innerRef={collapseEl}
            in={expanded}
            collapsedHeight={expandable ? `${maxHeight}px` : null}
            timeout={100}
          >
            {group.items.map((item, idx) => (
              <OrderItemWrapper key={item.id}>
                <OrderItem>
                  <div>
                    <Typography variant="caption">
                      <strong>{item.product_name}</strong>
                    </Typography>
                    <Typography variant="caption">
                      {item.product_description}
                    </Typography>
                  </div>
                  <div>
                    {(is_distributor || !isLocked) &&
                      Math.floor(item.quantity) > 1 && (
                        <OrderText variant="button" inline>
                          {`${Math.floor(item.quantity)}`} x{' '}
                          <strong>{formatPrice(item.unit_price_gross)}</strong>
                        </OrderText>
                      )}
                    {is_distributor || !isLocked ? (
                      <OrderText variant="button" inline>
                        <strong>{formatPrice(item.line_gross_price)}</strong>
                      </OrderText>
                    ) : (
                      ''
                    )}
                  </div>
                </OrderItem>
                {item.children.map((childItem, idx) => (
                  <OrderItem key={childItem.id}>
                    <div>
                      <Typography variant="caption">
                        <strong>&emsp;+ {childItem.product_name}</strong>
                      </Typography>
                    </div>
                    <div>
                      {(is_distributor || !isLocked) &&
                        Math.floor(childItem.quantity) > 1 && (
                          <OrderText variant="button" inline>
                            {`${Math.floor(childItem.quantity)}`} x{' '}
                            <strong>
                              {formatPrice(childItem.unit_price_gross)}
                            </strong>
                          </OrderText>
                        )}
                      {is_distributor || !isLocked ? (
                        <OrderText variant="button" inline>
                          <strong>
                            {formatPrice(childItem.line_gross_price)}
                          </strong>
                        </OrderText>
                      ) : (
                        ''
                      )}
                    </div>
                  </OrderItem>
                ))}
              </OrderItemWrapper>
            ))}
          </StyledCollapse>
          {!expanded && (
            <Grid container justify="center">
              <Button color="primary" onClick={() => setExpanded(true)}>
                <ChevronDownIcon /> Show All
              </Button>
            </Grid>
          )}
          <TotalInfo>
            <div>
              <Typography variant="h6">Subtotal:</Typography>
            </div>
            {is_distributor || !isLocked ? (
              <Typography variant="h6" id="checkout-total">
                {formatPrice(subtotal)}
              </Typography>
            ) : (
              <Button variant="h10">
                Pricing unavailable in your region, please contact a distributor
              </Button>
            )}
          </TotalInfo>
        </Fragment>
      ))}
    </>
  );
};

export default OrderItems;
